import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { fetchAds, fetchAdsList, fetchWaitingAds } from "../services/ads";
import { AdsListStatus } from "../types/api/ads";
import { AdsType } from "../types/api/ads";
import { SortDirection } from "../types/interfaces/sort";

export const useFetchAdsQuery = (adId: string) => {
  return useQuery({
    refetchOnWindowFocus: false,
    queryKey: ["adId", adId],
    queryFn: () => fetchAds(adId),
    retry: 0,
  });
};

export const useAdsListInfiniteQuery = (
  search: string = "",
  limit: number,
  isActive?: boolean,
  sort?: string,
  ordering?: SortDirection,
  status?: AdsListStatus,
  createdFrom?: string,
  createdTo?: string,
  priceFrom?: number,
  priceTo?: number,
  userId?: string,
  categoryId?: string,
  catalogId?: string,
  options?: {
    enabled?: boolean;
  }
) => {
  return useInfiniteQuery({
    queryKey: [
      "ads-infinite",
      search,
      limit,
      isActive,
      sort,
      ordering,
      status,
      createdFrom,
      createdTo,
      priceFrom,
      priceTo,
      userId,
      categoryId,
      catalogId,
    ],
    queryFn: ({ pageParam = { limit: limit, offset: 0 } }) =>
      fetchAdsList({
        search: search || undefined,
        limit: pageParam.limit,
        offset: pageParam.offset,
        isActive,
        sort,
        ordering,
        status,
        createdFrom,
        createdTo,
        priceFrom,
        priceTo,
        userId,
        categoryId,
        catalogId,
      }),
    initialPageParam: { limit: limit, offset: 0 },
    getNextPageParam: (lastPage, allPages) => {
      const totalLoaded = allPages.reduce(
        (acc, page) => acc + page.list.length,
        0
      );
      const hasMore = lastPage.list.length === limit;
      return hasMore ? { limit: limit, offset: totalLoaded } : undefined;
    },
    retry: 0,
    enabled: options?.enabled ?? true,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
  });
};

export const useAdsListQuery = (
  search: string = "",
  page: number,
  limit: number,
  isActive?: boolean,
  sort?: string,
  ordering?: string,
  status?: AdsListStatus,
  createdFrom?: string,
  createdTo?: string,
  priceFrom?: number,
  priceTo?: number,
  userId?: string,
  categoryId?: string,
  catalogId?: string,
  options?: {
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: [
      "ads",
      search,
      page,
      limit,
      isActive,
      sort,
      ordering,
      status,
      createdFrom,
      createdTo,
      priceFrom,
      priceTo,
      userId,
      categoryId,
      catalogId,
    ],
    queryFn: () =>
      fetchAdsList({
        search: search || undefined,
        limit,
        offset: limit === 1 ? page : (page - 1) * limit,
        isActive,
        sort,
        ordering,
        status,
        createdFrom,
        createdTo,
        priceFrom,
        priceTo,
        userId,
        categoryId,
        catalogId,
      }),
    retry: 0,
    enabled: options?.enabled ?? true,
  });
};

export const useWaitingAdsQuery = (
  page: number,
  limit: number,
  sort?: string,
  ordering?: string,
  categoryId?: string,
  catalogId?: string,
  options?: {
    enabled?: boolean;
  }
) => {
  return useQuery({
    queryKey: ["ads", "waiting", page, limit, sort, ordering],
    queryFn: () =>
      fetchWaitingAds({
        limit,
        offset: (page - 1) * limit,
        sort,
        ordering,
        categoryId,
        catalogId,
      }),
    retry: 0,
    enabled: options?.enabled ?? true,
  });
};

// Единый хук, который определяет какие именно загружать - размещенные, требуют подтверждения или в архиве
export const useAdsQuery = (
  adsType: string,
  search: string = "",
  page: number,
  limit: number,
  isActive?: boolean,
  sort?: string,
  ordering?: string,
  status?: AdsListStatus,
  createdFrom?: string,
  createdTo?: string,
  priceFrom?: number,
  priceTo?: number,
  userId?: string,
  categoryId?: string,
  catalogId?: string,
  options?: {
    enabled?: boolean;
  }
) => {
  const type = adsType as AdsType;
  const isShowWaiting = type === "requiresConfirmation";
  const isEnabled = options?.enabled ?? true;
  const requestStatus = type === "posted" ? status : AdsListStatus.ARCHIVED;

  const adsListQuery = useAdsListQuery(
    search,
    page,
    limit,
    isActive,
    sort,
    ordering,
    requestStatus,
    createdFrom,
    createdTo,
    priceFrom,
    priceTo,
    userId,
    categoryId,
    catalogId,
    {
      enabled: !isShowWaiting && isEnabled,
    }
  );

  const waitingAdsQuery = useWaitingAdsQuery(
    page,
    limit,
    sort,
    ordering,
    categoryId,
    catalogId,
    {
      enabled: isShowWaiting && isEnabled,
    }
  );
  return isShowWaiting ? waitingAdsQuery : adsListQuery;
};
